import { useState, useMemo } from 'react';
import { SortDirection } from '../GentlyTable';

export interface UseTableSortProps<T> {
  data: T[];
  serverSideSorting?: {
    sortBy: string;
    sortDirection: 'asc' | 'desc';
    onSort: (columnId: string, direction: 'asc' | 'desc') => void;
  };
  disableClientSideSort?: boolean;
  defaultSort?: {
    sortBy: keyof T;
    sortDirection: SortDirection;
  };
}

export function useTableSort<T extends Record<string, unknown>>({
  data,
  serverSideSorting,
  disableClientSideSort = false,
  defaultSort,
}: UseTableSortProps<T>) {
  const [sortBy, setSortBy] = useState<keyof T | null>(
    defaultSort?.sortBy || null,
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    defaultSort?.sortDirection || 'desc',
  );

  const sortedData = useMemo(() => {
    if (!data || serverSideSorting || disableClientSideSort) {
      // If server-side sorting is enabled or client-side sorting is disabled, don't sort client-side
      return data;
    }

    if (!sortBy) {
      return data;
    }

    return [...data].sort((a, b) => {
      // Helper to get nested property value (e.g., "supplier.name")
      const getNestedValue = (obj: T, key: keyof T) => {
        const keyStr = String(key);
        if (keyStr.includes('.')) {
          // For nested properties like "supplier.name"
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return keyStr.split('.').reduce((o: any, i) => o?.[i], obj);
        }
        return obj[key];
      };

      let aValue = getNestedValue(a, sortBy);
      let bValue = getNestedValue(b, sortBy);

      // Helper to extract sortable value from cell objects
      const extractSortValue = (
        cellValue: unknown,
      ): string | number | null | undefined => {
        if (
          !cellValue ||
          typeof cellValue !== 'object' ||
          !('value' in cellValue)
        ) {
          return cellValue as string | number | null | undefined;
        }

        const cell = cellValue as {
          value?: unknown;
          formattedValue?: string | null;
        };
        const formatted = cell.formattedValue;

        // If formattedValue is a non-numeric, non-empty string (like a code), use it
        if (
          typeof formatted === 'string' &&
          formatted !== '' &&
          formatted !== '-' &&
          isNaN(Number(formatted.replace(/[$,]/g, '')))
        ) {
          return formatted;
        }

        // Otherwise use the numeric value if available
        if (typeof cell.value === 'number') {
          return cell.value;
        }

        // Fall back to formatted value if it's a string
        if (typeof formatted === 'string' && formatted !== '-') {
          return formatted;
        }

        return null;
      };

      aValue = extractSortValue(aValue);
      bValue = extractSortValue(bValue);

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      let comparison = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        // Case-insensitive string comparison
        comparison = aValue.localeCompare(bValue, undefined, {
          sensitivity: 'base',
        });
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else if (
        aValue &&
        bValue &&
        typeof aValue === 'object' &&
        typeof bValue === 'object' &&
        'getTime' in aValue &&
        'getTime' in bValue
      ) {
        comparison =
          (aValue as unknown as Date).getTime() -
          (bValue as unknown as Date).getTime();
      } else {
        // Fallback: convert to string and compare case-insensitively
        comparison = String(aValue).localeCompare(String(bValue), undefined, {
          sensitivity: 'base',
        });
      }

      return sortDirection === 'desc' ? -comparison : comparison;
    });
  }, [data, sortBy, sortDirection, serverSideSorting, disableClientSideSort]);

  const handleSort = (columnId: keyof T, sortable?: boolean) => {
    if (!sortable) {
      return;
    }

    // Use server-side sorting if provided
    if (serverSideSorting) {
      const newDirection =
        serverSideSorting.sortBy === String(columnId) &&
        serverSideSorting.sortDirection === 'asc'
          ? 'desc'
          : 'asc';
      serverSideSorting.onSort(String(columnId), newDirection);
      return;
    }

    // Fallback to client-side sorting
    // 3-state cycle: desc → asc → none (original order)
    if (sortBy === columnId) {
      if (sortDirection === 'desc') {
        setSortDirection('asc');
      } else {
        // Was asc, clear sort to return to original order
        setSortBy(null);
        setSortDirection('asc');
      }
    } else {
      // New column: start with descending
      setSortBy(columnId);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (columnId: keyof T) => {
    const isActive = serverSideSorting
      ? serverSideSorting.sortBy === String(columnId)
      : sortBy === columnId;

    if (!isActive) {
      return null;
    }

    const direction = serverSideSorting
      ? serverSideSorting.sortBy === String(columnId)
        ? serverSideSorting.sortDirection
        : 'asc'
      : sortBy === columnId
        ? sortDirection
        : 'asc';

    return direction;
  };

  return {
    sortBy,
    sortDirection,
    sortedData,
    handleSort,
    getSortIcon,
  };
}
